import { ChevronRightIcon } from '@heroicons/react/outline';
import { Skeleton, Typography } from 'antd';
import useBreakpoint from 'antd/lib/grid/hooks/useBreakpoint';
import LeftArrow from 'assets/LeftArror';
import clsx from 'clsx';
import { motion } from 'framer-motion';
import Link from 'next/link';
import { useRouter } from 'next/router';
import React, { FC, ReactNode, useEffect, useRef, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { ShouldRender } from '../../../shared/basic/ShouldRender';

export interface HomepageSliderProps {
  children: ReactNode;
  heading: string;
  subHeading?: string;
  subHeadingLink?: string;
  isBook?: boolean;
  onPagination?: () => void;
  setIsPaginateShift?: (value: boolean) => void;
  isPaginateShift?: boolean;
  loading?: boolean;
  total?: number;
  imageWidths?: number[];
  notBookLandingPage?: boolean;
  isLive?: boolean;
  subHeadingCol?: string;
  headingClass?: string;
  parantClassName?: string;
  hideBars?: boolean;
  hidePaginationBars?: boolean;
}

type SlideProps = {
  totalRecords: number;
  containerWidth: number;
  imageWidths?: number[];
};

const calculateSlides = ({
  totalRecords,
  containerWidth,
  imageWidths = [120, 140, 200],
}: SlideProps): number => {
  if (
    totalRecords < 1 ||
    totalRecords === undefined ||
    !containerWidth ||
    !imageWidths.length
  ) {
    return 0;
  }

  let imageWidth = imageWidths[0];

  // Adjust image width based on screen size (breakpoints)
  if (typeof window !== 'undefined') {
    if (window.innerWidth >= 1280) {
      imageWidth = imageWidths[2]; // Use largest width for xl breakpoint
    } else if (window.innerWidth >= 1024) {
      imageWidth = imageWidths[1];
    }
  }

  // Calculate how many images fit in one slide (per screen)
  const imagesPerSlide =
    imageWidth && containerWidth && Math.floor(containerWidth / imageWidth);

  // Calculate total slides needed
  const totalSlides =
    imagesPerSlide && totalRecords && Math.ceil(totalRecords / imagesPerSlide);

  return totalSlides || 0;
};

const HomepageSlider: FC<HomepageSliderProps> = ({
  children,
  heading,
  isBook = false,
  onPagination,
  isPaginateShift = false,
  setIsPaginateShift,
  loading = false,
  total,
  imageWidths,
  notBookLandingPage = true,
  subHeading,
  subHeadingLink,
  subHeadingCol,
  headingClass,
  parantClassName,
  hideBars = false,
  hidePaginationBars = false,
}) => {
  const sliderRef = useRef<HTMLDivElement | null>(null);
  const [isAtLeftmost, setIsAtLeftmost] = useState(true);
  const [isAtRightmost, setIsAtRightmost] = useState(false);
  const [windowWidth, setWindowWidth] = useState<number>(window.innerWidth);
  const [leftPadding, setLeftPadding] = useState<number>(44);
  const [onHover, setOnHover] = useState(false);
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [containerWidth, setContainerWidth] = useState<number>(0);
  const totalSlides = calculateSlides({
    totalRecords: total || 1,
    containerWidth,
    imageWidths,
  });
  const router = useRouter();
  const screens = useBreakpoint();
  const updateWindowWidth = () => {
    setWindowWidth(window.innerWidth);
  };
  const scrollSlider = (direction: 'left' | 'right') => {
    if (!sliderRef.current) return;
    const { scrollLeft, clientWidth } = sliderRef.current;
    const scrollTo =
      direction === 'left'
        ? scrollLeft - clientWidth - 20
        : scrollLeft + clientWidth - 20;

    sliderRef.current.scrollTo({ left: scrollTo, behavior: 'smooth' });
    setLeftPadding(direction === 'left' ? 12 : 0);

    const newPage =
      direction === 'right'
        ? Math.min(currentPage + 1, totalSlides - 1)
        : Math.max(currentPage - 1, 0);

    if (direction === 'right' && !isAtRightmost) {
      onPagination?.();
      setCurrentPage(newPage);
    } else {
      setCurrentPage(newPage);
    }
  };

  useEffect(() => {
    setTimeout(() => {
      setWindowWidth(window.innerWidth);
      if (sliderRef.current) {
        sliderRef.current.scrollLeft = 0;
      }

      const handleScroll = () => {
        if (sliderRef.current) {
          const isLeftmost = sliderRef.current.scrollLeft === 0;
          const isRightmost =
            sliderRef.current.scrollLeft + sliderRef.current.clientWidth + 1 >=
            sliderRef.current.scrollWidth;

          setIsAtLeftmost(isLeftmost);
          setIsAtRightmost(isRightmost);
        }
      };

      sliderRef.current?.addEventListener('scroll', handleScroll);

      window.addEventListener('resize', updateWindowWidth);

      return () => {
        sliderRef.current?.removeEventListener('scroll', handleScroll);
        window.removeEventListener('resize', updateWindowWidth);
      };
    }, 0);
  }, []);

  useEffect(() => {
    if (isAtRightmost) {
      onPagination?.();
      setIsPaginateShift?.(true);
    }
  }, [isAtRightmost]);

  useEffect(() => {
    if (isPaginateShift) {
      setIsPaginateShift?.(false);
    }
  }, [isPaginateShift]);

  useEffect(() => {
    if (sliderRef.current) {
      setContainerWidth(sliderRef.current.offsetWidth);
    }
  }, [sliderRef.current?.offsetWidth]);

  const isExceedingWindowWidth = notBookLandingPage
    ? windowWidth !== undefined &&
      sliderRef.current?.scrollWidth !== undefined &&
      windowWidth < sliderRef.current?.scrollWidth
    : sliderRef.current?.scrollWidth !== undefined &&
      containerWidth < sliderRef.current?.scrollWidth;

  return (
    <div
      onMouseEnter={() => setOnHover(true)}
      onMouseLeave={() => setOnHover(false)}
      className='text-fadedWhite py-3'
    >
      <Typography.Title level={1} className='hidden'>
        H1 Tag
      </Typography.Title>
      <div className={clsx('mb-3 lg:mb-3', 'flex justify-between')}>
        <ShouldRender check={loading}>
          <div className='flex flex-col'>
            <div
              className={clsx(
                notBookLandingPage ? 'pl-3 md:px-5 lg:px-6 xl:px-12' : 'px-0',
                'flex text-white pb-0 font-semibold capitalize gap-x-3 md:gap-x-4 items-center',
              )}
            >
              <Typography.Title
                level={2}
                onClick={() => {
                  subHeading === 'Explore All' &&
                    subHeadingLink &&
                    router.push(subHeadingLink);
                }}
                className={twMerge(
                  clsx(
                    subHeading === 'Explore All' &&
                      'cursor-pointer hover:underline',
                    'text-base md:text-xl mb-0 lg:mt-1 xl:text-xl text-white',
                  ),
                  headingClass,
                )}
              >
                {heading}
              </Typography.Title>
              <ShouldRender
                check={
                  subHeadingLink &&
                  subHeading &&
                  (subHeading === 'Explore All' ? isExceedingWindowWidth : true)
                }
              >
                <Link
                  href={subHeadingLink || ''}
                  className='text-primary text-xs md:text-[13px] flex items-center gap-x-1.5 z-0 hover:underline font-medium italic translate-y-[1.2px]'
                >
                  {subHeading}{' '}
                  <ChevronRightIcon className='w-3 md:w-3.5 h-3 md:h-3.5' />
                </Link>
              </ShouldRender>
            </div>
            <ShouldRender check={subHeadingCol}>
              <div
                className={clsx(
                  notBookLandingPage ? 'pl-3 md:px-5 lg:px-6 xl:px-12' : 'px-0',
                  'flex text-textLightGray pb-0 md:mt-1 font-light capitalize gap-x-4 text-xs md:text-sm items-center',
                )}
              >
                {subHeadingCol}
              </div>
            </ShouldRender>
          </div>
        </ShouldRender>
        <ShouldRender check={!loading}>
          <Skeleton.Button
            style={{
              width: '150px',
              height: '28px',
              fontSize: '7px',
            }}
            active
            className={clsx(
              notBookLandingPage ? 'md:px-5 lg:px-6 xl:px-12 pl-4' : '',
              'rounded-sm transition-transform transform duration-500',
            )}
          />
        </ShouldRender>
        <ShouldRender
          check={
            !hideBars &&
            !hidePaginationBars &&
            totalSlides &&
            typeof totalSlides === 'number' &&
            totalSlides > 1 &&
            totalSlides !== Infinity &&
            !isNaN(totalSlides) &&
            Number.isInteger(totalSlides) &&
            totalSlides > 0 &&
            screens.lg
          }
        >
          <div className='flex justify-center items-center gap-[2px] mr-10'>
            {Array.from({ length: totalSlides }).map((_, index) => (
              <motion.div
                key={index}
                initial={{
                  width: currentPage === index ? 20 : 10,
                }}
                animate={{
                  width: currentPage === index ? 20 : 10,
                }}
                transition={{ duration: 0.3, ease: 'easeInOut' }}
                style={{
                  background: currentPage === index ? '#AAAAAA' : '#4D4D4D',
                  height: '3px',
                }}
              />
            ))}
          </div>
        </ShouldRender>
      </div>
      <ShouldRender check={screens?.lg}>
        <div
          style={{ scrollBehavior: 'smooth', scrollbarWidth: 'none' }}
          className='flex relative'
        >
          <div className='absolute z-30 h-full top-0 left-0'>
            <ShouldRender check={!onHover && !isAtLeftmost}>
              <div
                style={{
                  background:
                    'linear-gradient(270deg, rgba(0, 0, 0, 0.00) 0%, rgba(0, 0, 0, 0.60) 100%)',
                }}
                className='absolute z-0 h-full left-0 top-0 flex justify-end items-center gap-2.5 w-[48px]'
              ></div>
            </ShouldRender>
            <ShouldRender check={onHover && !isAtLeftmost}>
              <div className='relative h-full z-10'>
                <div
                  style={{
                    background:
                      'linear-gradient(270deg, rgba(0, 0, 0, 0.00) 0%, rgba(0, 0, 0, 0.20) 100%), linear-gradient(270deg, rgba(0, 0, 0, 0.00) 0%, rgba(0, 0, 0, 0.70) 100%)',
                  }}
                  className='absolute h-full left-0 w-[48px]'
                >
                  <div className='flex items-center h-full justify-center px-4 md:px-8 rounded-r-2xl'></div>
                </div>
                <button
                  disabled={isAtLeftmost}
                  onClick={() => scrollSlider('left')}
                  className={clsx(
                    isBook ? 'top-[45%]' : 'top-[40%]',
                    'z-10 absolute left-0 w-8 h-8 xl:w-10 xl:h-10 rotate-180 rounded-full disabled:cursor-not-allowed flex justify-center items-center disabled:text-gray-500 text-fadedWhite transition-transform duration-300 ease-in-out cursor-pointer hover:scale-125',
                  )}
                >
                  <LeftArrow />
                </button>
              </div>
            </ShouldRender>
          </div>
          <div className='w-full relative'>
            <span
              ref={sliderRef}
              className={twMerge(
                clsx(
                  notBookLandingPage ? 'px-4 md:px-5 lg:px-6 xl:px-12' : 'px-0',
                  'flex overflow-x-auto gap-x-1 h-full',
                ),
                parantClassName,
              )}
              style={{
                scrollBehavior: 'smooth',
                scrollbarWidth: 'none',
                paddingLeft: `${isAtLeftmost ? 44 : leftPadding}px`,
                paddingRight: `${isAtRightmost && 44}px`,
              }}
            >
              {children}
            </span>

            <ShouldRender
              check={!isAtRightmost && isExceedingWindowWidth && !onHover}
            >
              <div
                style={{
                  background:
                    'linear-gradient(270deg, rgba(0, 0, 0, 0.60) 0%, rgba(0, 0, 0, 0.00) 100%)',
                }}
                className='absolute z-0 h-full right-0 top-0 flex justify-end items-center gap-2.5 w-[48px]'
              ></div>
            </ShouldRender>
          </div>
          <div className='absolute z-30 h-full top-0 right-0'>
            <ShouldRender
              check={onHover && !isAtRightmost && isExceedingWindowWidth}
            >
              <div className='relative h-full'>
                <div className='absolute h-full right-0 w-[48px]'>
                  <div
                    style={{
                      background:
                        'linear-gradient(270deg, rgba(0, 0, 0, 0.20) 0%, rgba(0, 0, 0, 0.00) 100%), linear-gradient(270deg, rgba(0, 0, 0, 0.70) 0%, rgba(0, 0, 0, 0.00) 100%)',
                    }}
                    className='flex items-center h-full justify-center px-4 md:px-8 rounded-l-2xl'
                  ></div>
                </div>
                <button
                  disabled={isAtRightmost}
                  onClick={() => scrollSlider('right')}
                  className={clsx(
                    isBook ? 'top-[45%]' : 'top-[40%]',
                    'z-10 absolute right-4 w-8 h-8 xl:w-10 xl:h-10 rounded-full disabled:cursor-not-allowed flex justify-center items-center disabled:text-gray-500 text-fadedWhite transition-transform duration-300 ease-in-out cursor-pointer hover:scale-125',
                  )}
                >
                  <LeftArrow />
                </button>
              </div>
            </ShouldRender>
          </div>
        </div>
      </ShouldRender>
      <ShouldRender check={!screens?.lg}>
        <div className='w-full relative'>
          <ShouldRender check={!isAtLeftmost}>
            <div
              style={{
                background:
                  'linear-gradient(270deg, rgba(0, 0, 0, 0.00) 0%, rgba(0, 0, 0, 0.60) 100%)',
              }}
              className='absolute z-0 h-full left-0 top-0 flex justify-end items-center gap-2.5 w-[48px]'
            ></div>
          </ShouldRender>
          <div
            ref={sliderRef}
            className={clsx(
              notBookLandingPage ? 'px-3 md:px-4 lg:px-5 xl:px-12' : '',
              'flex hide-scrollbar gap-x-2 h-full',
            )}
            style={{ scrollBehavior: 'smooth', scrollbarWidth: 'none' }}
          >
            {children}
          </div>
          <ShouldRender check={!isAtRightmost && isExceedingWindowWidth}>
            <div
              style={{
                background:
                  'linear-gradient(270deg, rgba(0, 0, 0, 0.60) 0%, rgba(0, 0, 0, 0.00) 100%)',
              }}
              className='absolute z-0 h-full right-0 top-0 flex justify-end items-center gap-2.5 w-[1.875rem]'
            ></div>
          </ShouldRender>
        </div>
      </ShouldRender>
    </div>
  );
};

export default HomepageSlider;
